import { useMemo } from 'react';
import type { Issue } from '../types';
import { burndown, type Day } from '../burndown';
import { type Sprint, type SprintState } from '../sprints';
import { MONTHS } from '../when';
import { useApp } from '../store';

/**
 * THE LINE — HOW MUCH IS LEFT, AGAINST HOW MUCH TIME IS LEFT.
 *
 * Stage 3 of *Working in sprints* in ROADMAP.md, and the first chart in this
 * app. The arithmetic is all in `src/burndown.ts` and tested there without a
 * browser; this file only draws it.
 *
 * DRAWN BY HAND, IN SVG, WITH NO CHARTING LIBRARY. Not stubbornness — three
 * reasons, in order. A chart library is 60–150KB for one line and one diagonal.
 * Every one of them wants its own colours, and this app's palette is two
 * complete themes of CSS variables that have been contrast-checked; a library
 * theme is a third set that drifts. And artifacts and the published app run
 * under a strict CSP where a CDN script simply does not load. Twenty lines of
 * `<polyline>` has none of those problems.
 *
 * THE CHART IS NOT THE ANSWER, IT IS THE EVIDENCE. The sentence above it says
 * what it means in words — ahead, behind, or too early to tell — because a
 * burndown is a shape people are taught to read, and somebody who has not been
 * taught should not have to be. It is also the only part a screen reader gets,
 * which is the same requirement arriving from a different direction.
 */

/** Room for the axis labels, in the same units as the plot. */
const PAD = { top: 10, right: 12, bottom: 26, left: 34 };
/*
 * THE VIEWBOX IS THE CHART'S OWN PROPORTIONS AND IT IS SCALED EVENLY — no
 * `preserveAspectRatio="none"`. Stretching a viewBox to whatever width the
 * panel happens to be scales x and y by different amounts, which does not just
 * change the shape: the stroke widths stretch with it, and the round dot
 * marking today becomes a visible oval. Drawn at these proportions and allowed
 * to scale as a whole, a 2px line is 2px wherever it is read.
 */
const W = 480;
const H = 170;

export default function Burndown({
  sprint,
  issues,
}: {
  sprint: Sprint;
  issues: Issue[];
}) {
  // The workspace working calendar (0216) — the ideal line descends on working days
  // only, holding flat across weekends and the workspace's holidays.
  const workCalendar = useApp((s) => s.workCalendar);
  const days = useMemo(() => burndown(issues, sprint, Date.now(), workCalendar), [issues, sprint, workCalendar]);
  const real = days.filter((d) => d.actual);
  const todayIs = real[real.length - 1];

  /*
   * NOTHING TO DRAW IS A SENTENCE, NOT AN EMPTY CHART. A pair of axes with no
   * line on them reads as broken; "nothing in it yet" reads as a fact about the
   * sprint, which is what it is.
   */
  if (!days.length || !todayIs) return null;
  const committed = days[0].remaining;
  const everSized = days.some((d) => d.remaining > 0);
  if (!everSized && todayIs.unsized === 0) return null;

  const top = Math.max(committed, ...days.map((d) => d.remaining), 1);
  const x = (n: number) => PAD.left + (n / Math.max(1, days.length - 1)) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - v / top) * (H - PAD.top - PAD.bottom);
  /*
   * INDEXED AGAINST THE WHOLE SPRINT, not against the list being drawn. The
   * real line is a shorter list than the ideal one — it stops at today — and
   * numbering its own points would stretch three days of work across the full
   * width, drawing every running sprint as though it were finishing exactly on
   * time.
   */
  const points = (list: Day[], pick: (d: Day) => number) =>
    list.map((d) => `${x(days.indexOf(d))},${y(pick(d))}`).join(' ');

  return (
    <div className="burn">
      <div className="burn-say">
        <strong>{reading(days, sprint.state)}</strong>
        {todayIs.unsized > 0 && (
          /*
           * SAID EVERY TIME, not only when it is most of the sprint. Unsized
           * work is the one thing that makes this chart quietly wrong, and the
           * person reading it is usually the person who can fix it in a press.
           */
          <span className="burn-caveat">
            {' '}
            {todayIs.unsized} {todayIs.unsized === 1 ? 'card has' : 'cards have'} no size, so{' '}
            {todayIs.unsized === 1 ? 'it is' : 'they are'} in the count and not in the line.
          </span>
        )}
      </div>
      <svg
        className="burn-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={described(days, committed, sprint.state)}
      >
        {/* The scale, at top and bottom only. A full grid on a chart this small
            is more ink than line. */}
        <line className="burn-axis" x1={PAD.left} y1={y(top)} x2={W - PAD.right} y2={y(top)} />
        <line className="burn-axis" x1={PAD.left} y1={y(0)} x2={W - PAD.right} y2={y(0)} />
        <text className="burn-tick" x={PAD.left - 6} y={y(top) + 4} textAnchor="end">
          {round(top)}
        </text>
        <text className="burn-tick" x={PAD.left - 6} y={y(0) + 4} textAnchor="end">
          0
        </text>

        {/* Where the sprint would be if it burned down evenly. Dashed, because
            it is a reference and not a measurement — a second solid line reads
            as a second set of facts. */}
        <polyline className="burn-ideal" points={points(days, (d) => d.ideal)} fill="none" />

        {/* What actually happened, stopping where the facts stop. */}
        <polyline className="burn-actual" points={points(real, (d) => d.remaining)} fill="none" />
        <circle className="burn-now" cx={x(days.indexOf(todayIs))} cy={y(todayIs.remaining)} r={3} />

        <text className="burn-tick" x={PAD.left} y={H - 8}>
          {short(days[0].date)}
        </text>
        <text className="burn-tick" x={W - PAD.right} y={H - 8} textAnchor="end">
          {short(days[days.length - 1].date)}
        </text>
      </svg>
    </div>
  );
}

const round = (n: number) => Math.round(n * 10) / 10;
/** "5 Aug" — the year is the sprint's own and saying it twice is noise. */
const short = (iso: string) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}`;
};

/**
 * THE SHAPE, IN WORDS.
 *
 * AGAINST THE IDEAL LINE ON THE SAME DAY, which is the only comparison that
 * means anything — "12 points left" is not good or bad without knowing that
 * there are two days to do it in.
 *
 * THE BAND IS WIDE, and deliberately the same argument as the capacity bands: a
 * burndown that says "behind" on day two of a fortnight is describing normal
 * work, because almost nothing is finished on day two of anything. Only a gap
 * worth acting on gets a word.
 */
function reading(days: Day[], state: SprintState): string {
  const real = days.filter((d) => d.actual);
  const now = real[real.length - 1];
  if (!now) return '';
  /*
   * A FINISHED SPRINT IS NOT ON TRACK OR BEHIND — IT IS OVER, and this said
   * "On track" on a sprint that had closed five points short. Present tense on
   * a closed fortnight is not a wording problem: it tells a retrospective the
   * sprint went fine when the chart directly above it shows a line that never
   * reached the bottom.
   */
  if (state === 'closed') {
    if (now.cards === 0) return 'Finished, with everything in it done.';
    return `Finished with ${round(now.remaining)} points unfinished, out of ${round(days[0].remaining)}.`;
  }
  if (now.remaining === 0 && now.cards === 0) return 'Everything in this sprint is finished.';
  /*
   * TOO EARLY TO TELL IS AN ANSWER. On the first day the ideal has barely
   * moved, so any real number sits above it and the honest reading is that the
   * sprint has not run long enough to have a shape yet. Saying "behind" on day
   * one is how a team learns to ignore the chart.
   */
  /*
   * "N POINTS LEFT, OF M" — never "N points in this sprint".
   *
   * `now.remaining` is what is LEFT, and calling it what is IN the sprint put a
   * number on this chart that contradicted the heading two lines above it: the
   * card said "12 of 48 points" while the chart said "36 points in this
   * sprint". Both describing the same fortnight, and only one of them true.
   * Saying both halves means the two lines agree by construction.
   */
  /*
   * COUNTED IN DAYS, NOT IN POINTS PLOTTED. The chart carries an opening mark
   * that shares day one's date, so `real.length` is 2 on the first day and the
   * honest "too early" reading stopped firing — day one started announcing
   * "behind", which is exactly how a team learns to ignore a chart.
   */
  const daysRun = new Set(real.map((d) => d.date)).size;
  if (daysRun < 2) {
    /*
     * "OF M" ONLY WHERE M IS A REAL STARTING FIGURE. A sprint whose work was
     * all added after it opened has a commitment of nothing, and "11 points
     * left, of 0" is a sentence that cannot be true. The scope warning above
     * the chart is what says that work arrived late; this line just stops
     * claiming a total it does not have.
     */
    const opened = round(days[0].remaining);
    const left = round(now.remaining);
    return opened > left
      ? `${left} story points left, of ${opened}. Too early to read a trend.`
      : `${left} story points left. Too early to read a trend.`;
  }
  const gap = now.remaining - now.ideal;
  const scale = Math.max(days[0].remaining, 1);
  if (gap > scale * 0.15) return `Behind — ${round(now.remaining)} story points left, more than the pace would put here.`;
  if (gap < -scale * 0.15) return `Ahead — ${round(now.remaining)} story points left, less than the pace would put here.`;
  return `On track — ${round(now.remaining)} story points left.`;
}

/**
 * The chart as a sentence, for anybody who cannot see it.
 *
 * The numbers rather than a description of the picture: "a line falling from
 * left to right" tells a screen-reader user nothing they can act on, where the
 * two figures and the direction tell them the same thing a sighted reader gets.
 */
function described(days: Day[], committed: number, state: SprintState): string {
  const real = days.filter((d) => d.actual);
  const now = real[real.length - 1];
  return `Burndown. Started with ${round(committed)} points; ${round(now?.remaining ?? 0)} left after ${real.length} of ${days.length} days. ${reading(days, state)}`;
}
